
import axios, { AxiosRequestConfig } from "axios";
import { MetadataField, Stats, URLField } from "./model";
import { getAccessToken } from "./userlogin"; 
import { BACKEND_API } from "./config";
import { requestOptimizer, createCacheKey, withTimeout } from "../utils/requestOptimizer";
import type {
  ConversationsResponse,
  ConversationDetailResponse,
  ActivityTrendResponse,
  FeedbackAnalysisResponse,
  FeedbackAnalysisTrendResponse,
  EngagementFunnelResponse,
  EngagementFunnelTrendResponse,
  SemanticTopicsResponse,
  SemanticKeywordsResponse,
  CategoryDistributionResponse,
  CategoryDistributionTrendResponse,
  CostTrendResponse,
  AITrendsResponse,
  DashboardDataResponse,
  ResponseTimeAnalyticsResponse,
  SessionQualityMetricsResponse,
  SourceDistributionResponse,
  QuestionComplexityResponse,
  CitationUsageResponse,
  HourlyActivityResponse
} from "../types/api-models";


export const api = axios.create({
  baseURL: BACKEND_API,
  timeout: 30000,
});

export async function authHeaders(): Promise<AxiosRequestConfig['headers']> {
  try {
    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error("Authentication required. Please login to continue.");
    }
    return {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    };
  } catch (error) {
    console.error("Failed to get access token:", error);
    throw new Error("Authentication failed. Please try logging in again.");
  }
}

export const fetchUserProfile = async () => {
  const headers = await authHeaders();
  try {
    const res = await api.get(`/api/fetchUserProfile`, { headers });
    return res.data;
  } catch (error) {
    console.error("Failed to fetch user profile:", error);
    return null;
  }
};

export const getStats = async (): Promise<Stats> => {
  const headers = await authHeaders();
  try {
    const res = await api.get(`/api/fetchStats`, { headers });
    return res.data;
  } catch (error: any) {
    console.error("Error fetching statistics:", error);
    throw error;
  }
};

export const uploadDocumentWithMetadata = async (formData: FormData) => {
  const accessToken = await getAccessToken();
  try {
    const res = await api.post(
      `/api/documents/upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          // DO NOT set content-type for FormData, Axios sets it automatically
        }
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error uploading document and metadata:", error);
    throw error;
  }
};

export const uploadUrl = async (urlFields: URLField[]) => {
  const headers = await authHeaders();
  try {
    const res = await api.post(`/api/documents`, urlFields, { headers });
    return res.data;
  } catch (error) {
    console.error("Error uploading URL:", error);
    throw error;
  }
};

export const fetchDocuments = async (
  page: number,
  pageSize: number,
  query: string,
  status: string
) => {
  const headers = await authHeaders();
  try {
    const res = await api.get(
      `/api/documents`,
      {
        headers,
        params: { page, pageSize, query, status, include_count: true }
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error fetching documents:", error);
    throw error;
  }
};


export const removeDocument = async (id: string) => {
  const headers = await authHeaders();
  try {
    const res = await api.delete(`/api/documents/${id}`, { headers });
    return res.data;
  } catch (error) {
    console.error("Error removing document:", error);
    throw error;
  }
};

export const refreshDocument = async (id: string) => {
  const headers = await authHeaders();
  try {
    const res = await api.post(`/api/documents/${id}/refresh`, {}, { headers });
    return res.data;
  } catch (error) {
    console.error("Error refreshing document:", error);
    throw error;
  }
};

export const updateDocumentMetadata = async (
  id: string,
  metadata: MetadataField[]
) => {
  const headers = await authHeaders();
  try {
    const res = await api.post(`/api/documents/${id}/metadata`, metadata, { headers });
    return res.data;
  } catch (error) {
    console.error("Error updating document metadata:", error);
    throw error;
  }
};

export const fetchChunks = async (id: string): Promise<{ page_content: string; metadata: any }[]> => {
  const headers = await authHeaders();
  try {
    // Longer timeout for chunks query (Azure Search can be slow with many chunks)
    const res = await api.get(`/api/documents/${id}/chunks`, { 
      headers,
      timeout: 60000  // 60 second timeout
    });
    return res.data.chunks || res.data;  // Handle wrapped response
  } catch (error) {
    console.error("Error fetching chunks:", error);
    throw error;
  }
};

export const getAnalyticsStats = async (
  period?: '24h' | '7d' | '30d',
  from_date?: string,
  to_date?: string
) => {
  const headers = await authHeaders();

  // Params logic: date range takes precedence
  let params: any = {};
  if (from_date && to_date) {
    params = { from_date, to_date };
  } else if (period) {
    params = { period };
  } else {
    params = { period: '24h' };
  }

  try {
    const res = await api.get(`/api/analytics/summary`, { headers, params });
    return res.data;
  } catch (error) {
    console.error("Error fetching analytics stats:", error);
    throw error;
  }
};






export const getConversations = async (
  period?: '30m' | '1h' | '3h' | '6h' | '12h' | '24h',
  from_date?: string,
  to_date?: string,
  search?: string,
  feedback?: 'positive' | 'negative' | 'none' | 'any',
  source?: 'Web' | 'Modal' | 'Others',
  page: number = 1,
  page_size: number = 10,
  report?: 'reported' | 'none'
): Promise<ConversationsResponse> => {
  const headers = await authHeaders();

  let params: any = { page, page_size };

  // Only add period OR dates, never both
  if (from_date && to_date) {
    params.from_date = from_date;
    params.to_date = to_date;
    // Don't add period when using custom dates
  } else {
    // Use period (with default fallback)
    params.period = period || '1h';
  }

  if (search) params.search = search;
  if (feedback) params.feedback = feedback;
  if (source) params.source = source;
  if (report) params.report = report;

  try {
    const res = await api.get<ConversationsResponse>('/api/analytics/conversations', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
};

export const getConversationDetail = async (
  conversationId: string
): Promise<ConversationDetailResponse> => {
  const headers = await authHeaders();

  try {
    const res = await api.get<ConversationDetailResponse>(
      `/api/analytics/conversations/${encodeURIComponent(conversationId)}`,
      { headers }
    );
    return res.data;
  } catch (error) {
    console.error('Error fetching conversation detail:', error);
    throw error;
  }
};

export const exportConversations = async (
  period?: '30m' | '1h' | '3h' | '6h' | '12h' | '24h',
  from_date?: string,
  to_date?: string,
  search?: string,
  feedback?: 'positive' | 'negative' | 'none' | 'any',
  source?: 'Web' | 'Modal' | 'Others',
  report?: 'reported' | 'none'
): Promise<Blob> => {
  const headers = await authHeaders();

  let params: any = {};

  // Only add period OR dates, never both
  if (from_date && to_date) {
    params.from_date = from_date;
    params.to_date = to_date;
    // Don't add period when using custom dates
  } else {
    // Use period (with default fallback)
    params.period = period || '1h';
  }

  if (search) params.search = search;
  if (feedback) params.feedback = feedback;
  if (source) params.source = source;
  if (report) params.report = report;

  try {
    const res = await api.get('/api/analytics/conversations/export', {
      headers,
      params,
      responseType: 'blob',
      timeout: 120000  // 120 second timeout - export fetches all conversations with full threads
    });
    return res.data;
  } catch (error) {
    console.error('Error exporting conversations:', error);
    throw error;
  }
};

export const getActivityTrend = async (
  aggregation: 'daily' | 'weekly' | 'monthly' = 'daily',
  from_date?: string,
  to_date?: string
): Promise<ActivityTrendResponse> => {
  const cacheKey = createCacheKey('/api/analytics/activity-trend', { aggregation, from_date, to_date });
  
  return requestOptimizer.deduplicateRequest(
    cacheKey,
    async () => {
      const headers = await authHeaders();

      let params: any = { aggregation };

      // Date range only for daily aggregation
      if (aggregation === 'daily') {
        if (!from_date || !to_date) {
          throw new Error('Date range required for daily aggregation');
        }
        params.from_date = from_date;
        params.to_date = to_date;
      }
      // Weekly and monthly use fixed ranges (no dates needed)

      try {
        const res = await withTimeout(
          api.get<ActivityTrendResponse>('/api/analytics/activity-trend', { headers, params }),
          10000
        );
        return res.data;
      } catch (error) {
        console.error('Error fetching activity trend:', error);
        throw error;
      }
    },
    60000 // 1 minute cache
  );
};

export const getFeedbackAnalysis = async (
  from_date: string,
  to_date: string
): Promise<FeedbackAnalysisResponse> => {
  const headers = await authHeaders();

  const params = { from_date, to_date };

  try {
    const res = await api.get<FeedbackAnalysisResponse>('/api/analytics/feedback-analysis', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching feedback analysis:', error);
    throw error;
  }
};

export const getFeedbackAnalysisTrend = async (
  aggregation: 'weekly' | 'monthly',
  from_date?: string,
  to_date?: string
): Promise<FeedbackAnalysisTrendResponse> => {
  const headers = await authHeaders();
  
  const params: any = { aggregation };
  if (from_date && to_date) {
    params.from_date = from_date;
    params.to_date = to_date;
  }
  
  try {
    const res = await api.get<FeedbackAnalysisTrendResponse>('/api/analytics/feedback-analysis-trend', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching feedback analysis trend:', error);
    throw error;
  }
};

export const getCategoryDistributionTrend = async (
  aggregation: 'weekly' | 'monthly',
  top_n: number = 5,
  from_date?: string,
  to_date?: string
): Promise<CategoryDistributionTrendResponse> => {
  const headers = await authHeaders();
  
  const params: any = { aggregation, top_n };
  if (from_date && to_date) {
    params.from_date = from_date;
    params.to_date = to_date;
  }
  
  try {
    const res = await api.get<CategoryDistributionTrendResponse>('/api/analytics/category-distribution-trend', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching category distribution trend:', error);
    throw error;
  }
};

export const getCostTrend = async (
  aggregation: 'daily' | 'weekly' | 'monthly',
  from_date: string,
  to_date: string
): Promise<CostTrendResponse> => {
  const cacheKey = createCacheKey('/api/analytics/cost-trend', { aggregation, from_date, to_date });
  
  return requestOptimizer.deduplicateRequest(
    cacheKey,
    async () => {
      const headers = await authHeaders();
      const params = { aggregation, from_date, to_date };

      try {
        const res = await withTimeout(
          api.get('/api/analytics/cost-trend', { headers, params }),
          10000 // 10 second timeout
        );
        return res.data;
      } catch (error) {
        console.error('Error fetching cost trend:', error);
        throw error;
      }
    },
    60000 // 1 minute cache TTL
  );
};

// Single dashboard API call - replaces 100+ individual requests
export const getDashboardData = async (): Promise<DashboardDataResponse> => {
  const cacheKey = createCacheKey('/api/analytics/dashboard', {});
  
  return requestOptimizer.deduplicateRequest(
    cacheKey,
    async () => {
      const headers = await authHeaders();

      try {
        const res = await withTimeout(
          api.get('/api/analytics/dashboard', { headers }),
          60000 // 60 second timeout for combined data (backend needs time to aggregate 10+ queries)
        );
        return res.data as DashboardDataResponse;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
      }
    },
    60000 // 1 minute cache TTL
  );
};

export const getEngagementFunnel = async (
  from_date: string,
  to_date: string
): Promise<EngagementFunnelResponse> => {
  const headers = await authHeaders();

  const params = { from_date, to_date };

  try {
    const res = await api.get<EngagementFunnelResponse>('/api/analytics/engagement-funnel', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching engagement funnel:', error);
    throw error;
  }
};

export const getEngagementFunnelTrend = async (
  aggregation: 'daily' | 'weekly' | 'monthly',
  from_date?: string,
  to_date?: string
): Promise<EngagementFunnelTrendResponse> => {
  const headers = await authHeaders();

  const params: any = { aggregation };
  
  // Only add dates for daily aggregation
  if (aggregation === 'daily' && from_date && to_date) {
    params.from_date = from_date;
    params.to_date = to_date;
  }

  try {
    const res = await api.get<EngagementFunnelTrendResponse>('/api/analytics/engagement-funnel-trend', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching engagement funnel trend:', error);
    throw error;
  }
};

// AI-Powered Semantic Topics
export const getSemanticTopics = async (
  from_date: string,
  to_date: string,
  limit: number = 10
): Promise<SemanticTopicsResponse> => {
  const headers = await authHeaders();

  const params = { from_date, to_date, limit };

  try {
    const res = await api.get<SemanticTopicsResponse>('/api/analytics/semantic-topics', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching semantic topics:', error);
    throw error;
  }
};

// AI-Powered Semantic Keywords (compatible with word cloud/bar chart)
export const getSemanticKeywords = async (
  from_date: string,
  to_date: string,
  limit: number = 50
): Promise<SemanticKeywordsResponse> => {
  const headers = await authHeaders();

  const params = { from_date, to_date, limit };

  try {
    const res = await api.get<SemanticKeywordsResponse>('/api/analytics/semantic-keywords', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching semantic keywords:', error);
    throw error;
  }
};

export const getCategoryDistribution = async (
  from_date: string,
  to_date: string
): Promise<CategoryDistributionResponse> => {
  const headers = await authHeaders();

  const params = { from_date, to_date };

  try {
    const res = await api.get<CategoryDistributionResponse>('/api/analytics/category-distribution', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching category distribution:', error);
    throw error;
  }
};

// AI Trends (Weekly/Monthly)
export const getAITrends = async (
  period: 'week' | 'month'
): Promise<AITrendsResponse> => {
  const headers = await authHeaders();

  const params = { period };

  try {
    const res = await api.get<AITrendsResponse>('/api/analytics/ai-trends', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching AI trends:', error);
    throw error;
  }
};

// Enhanced Analytics API Functions
export const getResponseTimeAnalytics = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<ResponseTimeAnalyticsResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<ResponseTimeAnalyticsResponse>('/api/analytics/response-time', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching response time analytics:', error);
    throw error;
  }
};

export const getSessionQualityMetrics = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<SessionQualityMetricsResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<SessionQualityMetricsResponse>('/api/analytics/session-quality', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching session quality metrics:', error);
    throw error;
  }
};

export const getSourceDistribution = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<SourceDistributionResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<SourceDistributionResponse>('/api/analytics/source-distribution', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching source distribution:', error);
    throw error;
  }
};

export const getQuestionComplexityAnalysis = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<QuestionComplexityResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<QuestionComplexityResponse>('/api/analytics/question-complexity', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching question complexity analysis:', error);
    throw error;
  }
};

export const getCitationUsageAnalytics = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<CitationUsageResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<CitationUsageResponse>('/api/analytics/citation-usage', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching citation usage analytics:', error);
    throw error;
  }
};

export const getHourlyActivityHeatmap = async (
  aggregation: string,
  from_date: string,
  to_date: string
): Promise<HourlyActivityResponse> => {
  const headers = await authHeaders();
  const params = { aggregation, from_date, to_date };

  try {
    const res = await api.get<HourlyActivityResponse>('/api/analytics/hourly-activity', { headers, params });
    return res.data;
  } catch (error) {
    console.error('Error fetching hourly activity heatmap:', error);
    throw error;
  }
};

// Webhook Client Management API
import type {
  WebhookClient,
  WebhookClientListResponse,
  WebhookClientCreateData
} from "../types/api-models";

export type { WebhookClient, WebhookClientListResponse, WebhookClientCreateData };

export const getWebhookClients = async (): Promise<WebhookClientListResponse> => {
  const headers = await authHeaders();
  try {
    const res = await api.get<WebhookClientListResponse>('/api/webhook-clients', { headers });
    return res.data;
  } catch (error) {
    console.error('Error fetching webhook clients:', error);
    throw error;
  }
};

export const createWebhookClient = async (clientData: WebhookClientCreateData): Promise<WebhookClient> => {
  const headers = await authHeaders();
  try {
    const res = await api.post<WebhookClient>('/api/webhook-clients', clientData, { headers });
    return res.data;
  } catch (error) {
    console.error('Error creating webhook client:', error);
    throw error;
  }
};

export const updateWebhookClient = async (clientId: string, clientData: WebhookClientCreateData): Promise<WebhookClient> => {
  const headers = await authHeaders();
  try {
    const res = await api.put<WebhookClient>(`/api/webhook-clients/${clientId}`, clientData, { headers });
    return res.data;
  } catch (error) {
    console.error('Error updating webhook client:', error);
    throw error;
  }
};

export const deleteWebhookClient = async (clientId: string): Promise<void> => {
  const headers = await authHeaders();
  try {
    await api.delete(`/api/webhook-clients/${clientId}`, { headers });
  } catch (error) {
    console.error('Error deleting webhook client:', error);
    throw error;
  }
};

export const toggleWebhookClient = async (clientId: string, enabled: boolean): Promise<void> => {
  const headers = await authHeaders();
  try {
    await api.patch(`/api/webhook-clients/${clientId}/toggle`, { enabled }, { headers });
  } catch (error) {
    console.error('Error toggling webhook client:', error);
    throw error;
  }
};